import React from "react";
import { InitialsAvatar, avatarColorOf } from "@/components/common/InitialsAvatar";
import { createPortal } from "react-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, UserCircle, Mail, Phone, Building2, MapPin } from "lucide-react";
import { dashboardProfileMock } from "@/data/dashboardProfileMockData";
// @ts-ignore

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function UserProfileModal({ isOpen, onClose }: UserProfileModalProps) {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!isOpen || !mounted) return null;

  const userData = {
    firstName: dashboardProfileMock.firstName,
    middleName: dashboardProfileMock.middleName,
    lastName: dashboardProfileMock.lastName,
    fullName: dashboardProfileMock.fullName,
    position: dashboardProfileMock.position,
    avatar: dashboardProfileMock.avatar,
    email: dashboardProfileMock.email,
    phone: dashboardProfileMock.phone,
    department: dashboardProfileMock.department,
    location: dashboardProfileMock.location,
  };

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      onClick={onClose}
    >
      <Card
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader className="flex-shrink-0 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-semibold text-gray-900">
              Профиль пользователя
            </CardTitle>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-md"
            >
              <X size={20} />
            </button>
          </div>
        </CardHeader>
        <CardContent className="p-6 ipad:p-8">
          <div className="space-y-6">
            {/* Основная информация */}
            <div className="flex flex-col ipad:flex-row items-center ipad:items-start gap-6 pb-6 border-b">
              <div className="flex-shrink-0">
                {userData.avatar ? (
                  <img
                    src={userData.avatar}
                    alt="User avatar"
                    className="w-24 h-24 ipad:w-32 ipad:h-32 rounded-full object-cover border-4 border-blue-200 shadow-lg"
                  />
                ) : (
                  <div
                    className="w-24 h-24 ipad:w-32 ipad:h-32 rounded-full flex items-center justify-center border-4 border-blue-200 shadow-lg text-white"
                    style={{ backgroundColor: avatarColorOf(userData.fullName) }}
                  >
                    <InitialsAvatar name={userData.fullName} fontSize={40} />
                  </div>
                )}
              </div>
              <div className="flex-1 text-center ipad:text-left">
                <h2 className="text-2xl ipad:text-3xl font-bold text-gray-900 mb-2">
                  {userData.fullName}
                </h2>
                <p className="text-lg text-blue-600 font-medium mb-4 whitespace-pre-line">
                  {userData.position}
                </p>
                <div className="flex items-center justify-center ipad:justify-start gap-2 text-sm text-gray-600">
                  <Building2 size={16} />
                  <span>{userData.department}</span>
                </div>
              </div>
            </div>

            {/* Детальная информация */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Личная информация
              </h3>

              <div className="grid grid-cols-1 ipad:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <UserCircle className="text-blue-600" size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 mb-1">Имя</div>
                    <div className="text-sm font-medium text-gray-900">
                      {userData.firstName}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <UserCircle className="text-blue-600" size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 mb-1">Отчество</div>
                    <div className="text-sm font-medium text-gray-900">
                      {userData.middleName}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <UserCircle className="text-blue-600" size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 mb-1">Фамилия</div>
                    <div className="text-sm font-medium text-gray-900">
                      {userData.lastName}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Building2 className="text-blue-600" size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 mb-1">Должность</div>
                    <div className="text-sm font-medium text-gray-900 whitespace-pre-line">
                      {userData.position}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Контактная информация */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Контактная информация
              </h3>

              <div className="space-y-3">
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Mail className="text-blue-600" size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 mb-1">Email</div>
                    <div className="text-sm font-medium text-gray-900">
                      {userData.email}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Phone className="text-blue-600" size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 mb-1">Телефон</div>
                    <div className="text-sm font-medium text-gray-900">
                      {userData.phone}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <MapPin className="text-blue-600" size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 mb-1">
                      Местоположение
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {userData.location}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Кнопка закрытия */}
            <div className="pt-4 border-t">
              <Button
                onClick={onClose}
                className="w-full bg-gradient-to-br from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
              >
                Закрыть
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return createPortal(modalContent, document.body);
}
